import type { KeyboardEvent, ReactNode } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { styled } from 'styled-components';

const ModalSC = styled(motion.div)`
  z-index: 999;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${(props) => props.theme.colors.shadows.erieBlack[80]};

  &:focus {
    outline: none;
  }
`;

export type Props = {
  children: ReactNode;
  isOpen: boolean;
  modalRoot: HTMLElement;
  onClose: () => void;
};

export const Modal = ({ children, isOpen, modalRoot, onClose }: Props) => {
  const modalRef = useRef<HTMLDivElement>(null);

  /*
   * prevent root from scrolling
   */
  useEffect(() => {
    const root = document.getElementById('root');

    if (root) {
      if (isOpen) {
        root.style.position = 'fixed';
      } else {
        root.style.position = 'relative';
      }
    }
  }, [isOpen]);

  /*
   * put focus on modal and scroll to top
   */
  useEffect(() => {
    if (modalRef.current !== null && isOpen) {
      modalRef.current.focus();
      window.scrollTo(0, 0);
    }
  }, [modalRef, isOpen]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <ModalSC
          key="modal"
          ref={modalRef}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          onKeyDown={(e) => handleKeyDown(e)}
          tabIndex={-1}
          transition={{ duration: 0.25 }}
        >
          {children}
        </ModalSC>
      )}
    </AnimatePresence>,
    modalRoot,
  );
};
